import { useEffect, useState } from 'react';
import { X, Crown, AlertTriangle, Loader2, CheckCircle, TrendingUp, TrendingDown, Trash2, Plus, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api, UserDomain } from '../lib/api';
import { trackEvent } from '../lib/analytics';

interface AccountSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AccountSettings({ isOpen, onClose }: AccountSettingsProps) {
  const { profile, refreshProfile, domainLimit, canConfigurePagePath } = useAuth();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelSuccess, setCancelSuccess] = useState(false);

  // Configured domains state
  const [domains, setDomains] = useState<UserDomain[]>([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [domainsLoadError, setDomainsLoadError] = useState('');
  const [domainInput, setDomainInput] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [domainError, setDomainError] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (domainLimit === 0) {
      setDomains([]);
      setDomainsLoadError('');
      return;
    }
    let cancelled = false;
    setDomainsLoading(true);
    setDomainsLoadError('');
    api.getUserDomains()
      .then(rows => { if (!cancelled) setDomains(rows); })
      .catch(err => {
        console.error('Failed to load domains:', err);
        if (!cancelled) setDomainsLoadError(err instanceof Error ? err.message : 'Failed to load domains.');
      })
      .finally(() => { if (!cancelled) setDomainsLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, domainLimit]);

  const handleAddDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    setDomainError('');
    if (!domainInput.trim()) {
      setDomainError('Please enter a domain');
      return;
    }
    setAddingDomain(true);
    try {
      const created = await api.addUserDomain(domainInput.trim());
      setDomains(prev => [created, ...prev]);
      setDomainInput('');
      trackEvent('domain_added', 'configured_domains', created.url);
      window.dispatchEvent(new CustomEvent('user-domains-changed'));
    } catch (err) {
      setDomainError(err instanceof Error ? err.message : 'Failed to add domain');
    } finally {
      setAddingDomain(false);
    }
  };

  const handleDeleteDomain = async (id: number) => {
    try {
      await api.deleteUserDomain(id);
      setDomains(prev => prev.filter(d => d.id !== id));
      setConfirmDeleteId(null);
      trackEvent('domain_removed', 'configured_domains', String(id));
      window.dispatchEvent(new CustomEvent('user-domains-changed'));
    } catch (err) {
      setDomainError(err instanceof Error ? err.message : 'Failed to remove domain');
    }
  };

  if (!isOpen) return null;

  const isPaidTier = profile?.subscriptionTier && profile.subscriptionTier !== 'free';
  const currentTier = profile?.subscriptionTier || 'free';

  const getTierLabel = () => {
    const tierLabels: { [key: string]: string } = {
      'free': 'Free',
      'starter': 'Starter',
      'professional': 'Professional',
      'business': 'Business',
      'enterprise': 'Enterprise',
    };
    return tierLabels[currentTier] || 'Free';
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    setCancelError('');

    trackEvent('subscription_cancelled', 'subscription', currentTier);

    try {
      const token = api.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch('/api/subscription/cancel', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }

      setCancelSuccess(true);
      await refreshProfile();

      setTimeout(() => {
        setShowCancelConfirm(false);
        onClose();
        setCancelSuccess(false);
      }, 2000);
    } catch (error) {
      setCancelError(error instanceof Error ? error.message : 'Failed to cancel subscription');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleUpgrade = () => {
    trackEvent('upgrade_clicked', 'subscription', 'from_account_settings');
    window.location.href = '/?pricing=true';
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Account Settings</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Current Plan</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  {isPaidTier && <Crown className="w-5 h-5 text-yellow-500" />}
                  <span className="font-medium text-gray-900">{getTierLabel()} Plan</span>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  {profile?.email}
                </p>
                {profile?.analysisLimit === -1 && (
                  <p className="text-sm text-green-600">
                    Unlimited analyses per month
                  </p>
                )}
                {profile && profile.analysisLimit > 0 && (
                  <p className="text-sm text-gray-600">
                    {profile.analysisCount || 0} / {profile.analysisLimit} analyses used this month
                  </p>
                )}
                {profile?.subscriptionStatus && (
                  <p className="text-xs text-gray-500 mt-2 capitalize">
                    Status: {profile.subscriptionStatus}
                  </p>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Configured Domains</h3>
              {domainLimit === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700">
                  Save your most-scanned domains for one-click analysis.{' '}
                  <button
                    onClick={handleUpgrade}
                    className="text-black underline font-medium hover:text-gray-700"
                  >
                    Upgrade to a paid plan
                  </button>{' '}
                  to configure domains.
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500">
                    {domainLimit === -1
                      ? `${domains.length} configured · unlimited`
                      : `${domains.length} of ${domainLimit} used`}
                    {canConfigurePagePath && ' · page paths allowed'}
                  </p>

                  {domainsLoadError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                      Couldn't load your domains: {domainsLoadError}
                    </div>
                  )}

                  {domainsLoading ? (
                    <div className="text-sm text-gray-500 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading domains...
                    </div>
                  ) : domains.length === 0 ? (
                    <p className="text-sm text-gray-500">No domains configured yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {domains.map((d) => (
                        <li
                          key={d.id}
                          className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <Globe className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            <span className="text-sm text-gray-900 truncate" title={d.url}>{d.url}</span>
                            {d.isPage && (
                              <span className="text-[10px] uppercase tracking-wide bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded flex-shrink-0">
                                page
                              </span>
                            )}
                          </div>
                          {confirmDeleteId === d.id ? (
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                onClick={() => handleDeleteDomain(d.id)}
                                className="text-xs bg-red-600 hover:bg-red-700 text-white rounded px-2 py-1 font-medium"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="text-xs text-gray-600 hover:text-gray-800"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDeleteId(d.id)}
                              className="text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                              aria-label={`Remove ${d.url}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  {(domainLimit === -1 || domains.length < domainLimit) ? (
                    <form onSubmit={handleAddDomain} className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={domainInput}
                          onChange={(e) => setDomainInput(e.target.value)}
                          placeholder={
                            canConfigurePagePath
                              ? 'example.com or example.com/blog/post-1'
                              : 'example.com'
                          }
                          disabled={addingDomain}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 disabled:bg-gray-50"
                        />
                        <button
                          type="submit"
                          disabled={addingDomain}
                          className="bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1"
                        >
                          {addingDomain ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                          Add
                        </button>
                      </div>
                      {domainError && (
                        <p className="text-xs text-red-600">{domainError}</p>
                      )}
                      {!canConfigurePagePath && (
                        <p className="text-xs text-gray-500">
                          Tip: Enterprise plans can configure individual page paths.
                        </p>
                      )}
                    </form>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                      You've reached your plan's domain limit.{' '}
                      <button onClick={handleUpgrade} className="underline font-medium hover:text-yellow-900">
                        Upgrade
                      </button>{' '}
                      to add more.
                    </div>
                  )}
                </div>
              )}
            </div>

            {!showCancelConfirm && !cancelSuccess && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Manage Subscription</h3>
                <div className="space-y-3">
                  {currentTier !== 'enterprise' && (
                    <button
                      onClick={handleUpgrade}
                      className="w-full bg-black hover:bg-gray-800 text-white rounded-lg px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <TrendingUp className="w-4 h-4" />
                      {isPaidTier ? 'Upgrade Plan' : 'View Premium Plans'}
                    </button>
                  )}
                  
                  {isPaidTier && (
                    <button
                      onClick={() => setShowCancelConfirm(true)}
                      className="w-full text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <TrendingDown className="w-4 h-4" />
                      Downgrade to Free
                    </button>
                  )}
                </div>
              </div>
            )}

            {showCancelConfirm && !cancelSuccess && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-4">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-900 mb-1">
                      Downgrade to Free Plan?
                    </h4>
                    <p className="text-sm text-red-700 mb-3">
                      Your subscription will be cancelled and your account will be downgraded to the Free plan. You'll lose access to premium features and be limited to 3 analyses per month.
                    </p>
                    {cancelError && (
                      <p className="text-sm text-red-700 mb-3 font-medium">
                        Error: {cancelError}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={handleCancelSubscription}
                        disabled={isCancelling}
                        className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        {isCancelling && <Loader2 className="w-4 h-4 animate-spin" />}
                        Yes, Downgrade to Free
                      </button>
                      <button
                        onClick={() => {
                          setShowCancelConfirm(false);
                          setCancelError('');
                        }}
                        disabled={isCancelling}
                        className="bg-white hover:bg-gray-50 disabled:bg-gray-100 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        Keep My Plan
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {cancelSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-green-900 mb-1">
                      Downgraded to Free Plan
                    </h4>
                    <p className="text-sm text-green-700">
                      Your subscription has been cancelled and your account is now on the Free plan.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
